import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck,
  Upload,
  Download,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  Archive,
  Info,
  Package,
} from "lucide-react";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import JSZip from "jszip";
import * as pdfjsLib from "pdfjs-dist";
import { PDFDocument } from "pdf-lib";

// Use CDN worker — works in CRA/webpack without ejecting
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ── Partner detection ────────────────────────────────────────────────────────
const PARTNERS = [
  { key: "VALMO",       label: "Valmo",       patterns: [/\bvalmo\b/i] },
  { key: "BLUEDART",    label: "Blue Dart",   patterns: [/blue[\s-]?dart/i] },
  { key: "DELHIVERY",   label: "Delhivery",   patterns: [/\bdelhivery\b/i] },
  { key: "ECOM_EXPRESS",label: "Ecom Express",patterns: [/ecom[\s]?express/i, /\becomexpress\b/i, /ecom[\s]?exp\b/i] },
  { key: "SHADOWFAX",   label: "Shadowfax",   patterns: [/\bshadowfax\b/i] },
  { key: "XPRESSBEES",  label: "Xpressbees",  patterns: [/xpress[\s]?bees/i, /express[\s]?bees/i, /\bxpressbees\b/i, /\bexpressbees\b/i] },
  { key: "EKART",       label: "Ekart",       patterns: [/\be-?kart\b/i] },
  { key: "DTDC",        label: "DTDC",        patterns: [/\bdtdc\b/i] },
  { key: "SMARTR",      label: "Smartr",      patterns: [/\bsmartr\b/i] },
  { key: "INDIA_POST",  label: "India Post",  patterns: [/india[\s]?post/i, /\bindiapost\b/i, /speed[\s]?post/i] },
];

function detectPartner(text) {
  for (const partner of PARTNERS) {
    for (const pattern of partner.patterns) {
      if (pattern.test(text)) return partner;
    }
  }
  return null;
}

// ── Card colour map per partner ──────────────────────────────────────────────
const PARTNER_STYLES = {
  VALMO:        { card: "bg-blue-50 border-blue-100",   badge: "bg-blue-100 text-blue-700",   dot: "bg-blue-500"   },
  BLUEDART:     { card: "bg-red-50 border-red-100",     badge: "bg-red-100 text-red-700",     dot: "bg-red-500"    },
  DELHIVERY:    { card: "bg-green-50 border-green-100", badge: "bg-green-100 text-green-700", dot: "bg-green-500"  },
  ECOM_EXPRESS: { card: "bg-orange-50 border-orange-100", badge: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  SHADOWFAX:    { card: "bg-indigo-50 border-indigo-100", badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  XPRESSBEES:   { card: "bg-yellow-50 border-yellow-100", badge: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" },
  EKART:        { card: "bg-teal-50 border-teal-100",   badge: "bg-teal-100 text-teal-700",   dot: "bg-teal-500"   },
  DTDC:         { card: "bg-rose-50 border-rose-100",   badge: "bg-rose-100 text-rose-700",   dot: "bg-rose-500"   },
  SMARTR:       { card: "bg-cyan-50 border-cyan-100",   badge: "bg-cyan-100 text-cyan-700",   dot: "bg-cyan-500"   },
  INDIA_POST:   { card: "bg-emerald-50 border-emerald-100", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  UNKNOWN:      { card: "bg-amber-50 border-amber-200", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-400"  },
};

function getStyle(key) {
  return PARTNER_STYLES[key] || PARTNER_STYLES.UNKNOWN;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function pageLabel(pages) {
  if (pages.length <= 6) return pages.map((p) => p + 1).join(", ");
  return pages.slice(0, 5).map((p) => p + 1).join(", ") + ` +${pages.length - 5} more`;
}

// ── Main component ───────────────────────────────────────────────────────────
export const LabelDeskPage = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [groups, setGroups] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const fileInputRef = useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const clearFile = () => {
    setFile(null);
    setGroups(null);
    setOrderStats(null);
    setProgress({ current: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Select + immediately scan ────────────────────────────────────────────────
  const handleFile = async (f) => {
    if (!f || processing) return;
    if (f.type !== "application/pdf") {
      toast.error("Only PDF files are accepted");
      return;
    }
    setFile(f);
    setGroups(null);
    setOrderStats(null);
    setProgress({ current: 0, total: 0 });
    setProcessing(true);

    try {
      const arrayBuffer = await f.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdfDoc = await loadingTask.promise;
      const totalPages = pdfDoc.numPages;

      setProgress({ current: 0, total: totalPages });

      const detected = {};
      let codCount = 0;

      for (let i = 1; i <= totalPages; i++) {
        const page = await pdfDoc.getPage(i);
        const textContent = await page.getTextContent();
        const text = textContent.items.map((item) => item.str).join(" ");

        const partner = detectPartner(text);
        const key = partner ? partner.key : "UNKNOWN";

        if (!detected[key]) {
          detected[key] = {
            partner: partner || { key: "UNKNOWN", label: "Unknown" },
            pages: [],
          };
        }
        detected[key].pages.push(i - 1); // pdf-lib uses 0-indexed

        if (/cash\s+on\s+delivery|\bcod\b/i.test(text)) codCount++;

        setProgress({ current: i, total: totalPages });
      }

      setGroups(detected);
      setOrderStats({ total: totalPages, cod: codCount, prepaid: totalPages - codCount });

      const knownCount = Object.keys(detected).filter((k) => k !== "UNKNOWN").length;
      toast.success(`Scan complete — ${knownCount} delivery partner${knownCount !== 1 ? "s" : ""} found`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to process PDF: " + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ── Download single partner PDF ─────────────────────────────────────────────
  const downloadPDF = async (key) => {
    if (!file || downloading || downloadingZip) return;
    setDownloading(key);

    try {
      const group = groups[key];
      const arrayBuffer = await file.arrayBuffer();
      const srcPdf = await PDFDocument.load(arrayBuffer);
      const newPdf = await PDFDocument.create();
      const copied = await newPdf.copyPages(srcPdf, group.pages);
      copied.forEach((p) => newPdf.addPage(p));
      const pdfBytes = await newPdf.save();

      const safeName = group.partner.label.replace(/\s+/g, "_");
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `LabelDesk_${safeName}_Labels.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${group.pages.length} label${group.pages.length !== 1 ? "s" : ""} for ${group.partner.label}`);
    } catch (err) {
      toast.error("Download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  // ── Download all as ZIP ─────────────────────────────────────────────────────
  const downloadZip = async () => {
    if (!file || downloadingZip || downloading || !groups) return;
    setDownloadingZip(true);

    try {
      const zip = new JSZip();
      const arrayBuffer = await file.arrayBuffer();
      const srcPdf = await PDFDocument.load(arrayBuffer);

      for (const [key, group] of Object.entries(groups)) {
        const newPdf = await PDFDocument.create();
        const copied = await newPdf.copyPages(srcPdf, group.pages);
        copied.forEach((p) => newPdf.addPage(p));
        const pdfBytes = await newPdf.save();
        const safeName = group.partner.label.replace(/\s+/g, "_");
        zip.file(`LabelDesk_${safeName}_Labels.pdf`, pdfBytes);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "LabelDesk_All_Labels.zip";
      a.click();
      URL.revokeObjectURL(url);

      toast.success("All labels downloaded as ZIP");
    } catch (err) {
      toast.error("ZIP download failed: " + err.message);
    } finally {
      setDownloadingZip(false);
    }
  };

  const totalPages = progress.total;
  const scannedPages = progress.current;
  const progressPct = totalPages > 0 ? Math.round((scannedPages / totalPages) * 100) : 0;
  const groupEntries = groups ? Object.entries(groups) : [];

  const knownEntries = groupEntries.filter(([k]) => k !== "UNKNOWN");
  const unknownEntry = groupEntries.find(([k]) => k === "UNKNOWN");
  const hasResults = groups !== null;

  return (
    <div className="space-y-6" data-testid="panel-label-desk">

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="rounded-3xl border border-purple-100 bg-white p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-purple-600" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg sm:text-xl font-extrabold text-slate-900 leading-tight">
              LabelDesk — Label Splitter
            </div>
            <div className="text-xs sm:text-sm text-slate-500 mt-1">
              Upload a multi-page Meesho label PDF, detect delivery partners on each page, and download separate PDFs per courier — all in your browser.
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Upload card ──────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.06 }}
        className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4"
      >
        <div className="text-sm font-semibold text-slate-700">Upload Label PDF</div>

        {/* Drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !processing && fileInputRef.current?.click()}
          className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 transition-all cursor-pointer select-none
            ${file
              ? "border-purple-200 bg-purple-50/40 cursor-default"
              : isDragging
              ? "border-purple-400 bg-purple-50 scale-[1.01]"
              : "border-slate-200 bg-slate-50/60 hover:border-purple-300 hover:bg-purple-50/30"
            }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {file ? (
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="font-semibold text-slate-800 text-sm break-all max-w-xs">{file.name}</div>
              <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>
              {!processing && (
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  className="mt-1 flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                <Upload className="w-6 h-6 text-slate-400" />
              </div>
              <div className="font-semibold text-slate-700 text-sm">
                {isDragging ? "Drop it here!" : "Drag & drop your PDF here"}
              </div>
              <div className="text-xs text-slate-400">or click to browse — PDF only</div>
            </div>
          )}
        </div>

      </motion.div>

      {/* ── Scanning progress ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {processing && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6"
          >
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Scanning page {scannedPages} of {totalPages}…</span>
                <span className="tabular-nums">{progressPct}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-purple-500 to-violet-500"
                  animate={{ width: `${progressPct}%` }}
                  transition={{ ease: "easeOut", duration: 0.2 }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Results ──────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {hasResults && !processing && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="space-y-5"
          >
            {/* ── Order summary stats ──────────────────────────────────────── */}
            {orderStats && (
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {[
                  { label: "Total Orders", value: orderStats.total, icon: Package, tint: "bg-purple-100 text-purple-600" },
                  { label: "Prepaid",      value: orderStats.prepaid, icon: CheckCircle2, tint: "bg-green-100 text-green-600" },
                  { label: "COD",          value: orderStats.cod, icon: Truck, tint: "bg-amber-100 text-amber-600" },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.06 }}
                    className="rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow"
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${stat.tint}`}>
                      <stat.icon className="w-4.5 h-4.5" strokeWidth={2.2} />
                    </div>
                    <div className="text-[11px] text-slate-500 font-medium">{stat.label}</div>
                    <div className="font-heading text-2xl font-extrabold text-slate-900 tabular-nums">{stat.value}</div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Download All ZIP */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-3xl border border-purple-100 bg-white p-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                  <Archive className="w-4.5 h-4.5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">Download All as ZIP</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {groupEntries.length} group{groupEntries.length !== 1 ? "s" : ""} · {totalPages} total label{totalPages !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
              <Button
                onClick={downloadZip}
                disabled={downloadingZip || !!downloading}
                className="w-full sm:w-auto min-h-[44px] rounded-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-md shadow-purple-600/25 disabled:opacity-50"
              >
                {downloadingZip ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bundling…</>
                ) : (
                  <><Archive className="w-4 h-4 mr-2" /> Download ZIP</>
                )}
              </Button>
            </div>

            {/* Unknown warning tip */}
            {unknownEntry && (
              <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  <span className="font-semibold">Tip:</span> {unknownEntry[1].pages.length} page{unknownEntry[1].pages.length !== 1 ? "s" : ""} could not be matched to a known carrier. Open those pages in your PDF viewer and check for a courier name — it may be printed as an image rather than selectable text.
                </p>
              </div>
            )}

            {/* Partner cards grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {knownEntries.map(([key, group], i) => {
                const style = getStyle(key);
                const isThisDownloading = downloading === key;
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className={`rounded-2xl border p-4 sm:p-5 flex flex-col gap-3 ${style.card}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${style.dot}`} />
                        <span className="font-heading font-extrabold text-slate-900 text-base leading-tight">
                          {group.partner.label}
                        </span>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${style.badge}`}>
                        {group.pages.length} label{group.pages.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <Package className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">Pages: {pageLabel(group.pages)}</span>
                    </div>

                    <Button
                      size="sm"
                      disabled={!!downloading || downloadingZip}
                      onClick={() => downloadPDF(key)}
                      className={`min-h-[44px] w-full rounded-full font-semibold text-sm transition-all disabled:opacity-50
                        ${isThisDownloading
                          ? "bg-purple-600 text-white"
                          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
                        }`}
                    >
                      {isThisDownloading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building PDF…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Download PDF</>
                      )}
                    </Button>
                  </motion.div>
                );
              })}

              {/* Unknown card — amber style */}
              {unknownEntry && (() => {
                const [, group] = unknownEntry;
                const style = getStyle("UNKNOWN");
                const isThisDownloading = downloading === "UNKNOWN";
                return (
                  <motion.div
                    key="UNKNOWN"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: knownEntries.length * 0.05 }}
                    className={`rounded-2xl border p-4 sm:p-5 flex flex-col gap-3 ${style.card}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="font-heading font-extrabold text-slate-900 text-base leading-tight">
                          Unknown
                        </span>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${style.badge}`}>
                        {group.pages.length} label{group.pages.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <Package className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">Pages: {pageLabel(group.pages)}</span>
                    </div>

                    <Button
                      size="sm"
                      disabled={!!downloading || downloadingZip}
                      onClick={() => downloadPDF("UNKNOWN")}
                      className={`min-h-[44px] w-full rounded-full font-semibold text-sm transition-all disabled:opacity-50
                        ${isThisDownloading
                          ? "bg-amber-500 text-white"
                          : "bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 shadow-sm"
                        }`}
                    >
                      {isThisDownloading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building PDF…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Download PDF</>
                      )}
                    </Button>
                  </motion.div>
                );
              })()}
            </div>

            {/* Privacy notice */}
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <Info className="w-4 h-4 text-slate-400 shrink-0" />
              <p className="text-[11px] text-slate-500">
                All processing happens entirely in your browser. Your PDF never leaves your device.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
